import { useNavigate, useSearch } from '@tanstack/react-location'
import Input from 'MediaManager/components/Form/Input'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { deleteDesignLayout } from 'MediaManager/services/designs'
import { getUnitType } from 'MediaManager/services/property'
import { useCallback, useMemo } from 'react'
import Button from 'shared/components/Button'
import DataTable from 'shared/components/DataTable'
import Loader from 'shared/components/Loader'
import { useRequest } from 'shared/hooks/useRequest'
import RoomDesignsTable from './components/RoomDesignsTable'
import { getStagingLivingLabel } from 'MediaManager/helpers/labels'
import { getUnitTypeDesignLayouts } from 'shared/helpers/levels'

function ManageLineDesignsScreen(props) {
  const { line } = useSearch()

  const navigate = useNavigate()

  const getData = useCallback(async () => {
    const { data } = await getUnitType(line)
    return data.unit_types[0]
  }, [line])

  const { data, loading, refresh } = useRequest(getData)

  const designLayoutsTableActions = [
    {
      label: 'Add design layout',
      action: () => {
        navigate({ to: `/design_layouts/add?line=${line}` })
      },
    },
  ]

  async function onDelete(id) {
    await deleteDesignLayout(id)
    refresh()
  }

  const designLayoutsTable = [
    {
      label: 'ID',
      data: (d) => <span className='underline'>{d.id}</span>,
    },
    {
      label: 'Name',
      data: (d) => <span>{d.name}{<span className='opacity-50'>{getStagingLivingLabel(d.design_layouts_configuration_options || {})}</span>}</span>,
    },
    {
      label: 'Level',
      data: (d) => d.level?.floor_number ?? 'All',
    },
    {
      label: 'Main Vendor',
      data: (d) => d.main_vendor.display_name,
    },
    {
      label: 'Package (ID + Name)',
      data: (d) => {
        let pack = d.package
        if (!pack) {
          pack = d.room_designs.map((r) => r.packages).flat()[0]?.package
        }
        return pack
          ? `ID: ${pack?.id} · ${pack?.name || pack?.display_name}${
              pack?.package_type ? ` · ${pack?.package_type}` : ''
            }`
          : '-'
      },
    },
    {
      label: 'Actions',
      data: (data) => (
        <div className="flex justify-end">
          <Button size="small" variant="secondary" rounded className="ml-[16px] !w-max !min-w-max">
            Edit
          </Button>
          <Button
            size="small"
            variant="secondary"
            rounded
            onClick={(e) => {
              e.stopPropagation()
              if (window.confirm('Are you sure you want to delete this design layout?')) {
                onDelete(data.id)
              }
            }}
            className="ml-[16px] !w-max !min-w-max"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ]

  const designLayouts = useMemo(() => {
    if (data) {
      return getUnitTypeDesignLayouts(data).map((d) => ({ ...d, level: data.unit_type_levels.find((l) => l.id === d.unit_type_level_id) }))
    }
    return []
  }, [data])

  const roomDesigns = useMemo(() => {
    if (data) {
      return data.unit_type_levels
        .map((l) => l.rooms.map(({ room_designs, ...r }) => room_designs.map((d) => ({ ...d, room: r })).flat()).flat())
        .flat()
    }
    return []
  })

  
  return (
    <ScreenLayout title="Line designs">
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      {!loading && (
        <>
          <div>
            <Input label="Property" disabled value={data.property.property_name} />
            <Input wrapClassName="mt-[16px]" label="Line" disabled value={data.name} />
          </div>
          <div className="mt-[16px]">
            <DataTable
              actions={designLayoutsTableActions}
              onRowClick={(data) => {
                navigate({ to: `/design_layouts/edit?line=${line}&id=${data.id}` })
              }}
              data={designLayouts}
              title="Design Layouts"
              structure={designLayoutsTable}
            />
          </div>
          <div className="mt-[16px]">
            <RoomDesignsTable
              onAdd={() => {
                navigate({ to: `/room_designs/add?line=${line}` })
              }}
              data={roomDesigns}
              onItemClick={(i) => {
                navigate({ to: `/room_designs/edit?line=${line}&id=${i.id}` })
              }}
            />
          </div>
        </>
      )}
    </ScreenLayout>
  )
}

export default ManageLineDesignsScreen
